import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { trackEvent } from "../../lib/matomo";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import DataService from "../../components/auth/Service";
import { useUser } from "../../context/UserContext";
import LogoBlue from "../../assets/icons/peppa_logo_blue.svg";
import LoginModern from "../login/LoginModern";
import SignUpFormModern from "../sign_up/sign_up_form/SignUpFormModern";
import { motion, AnimatePresence } from "framer-motion";
import { getPostAuthRedirectTarget } from "../../lib/authRedirect";

export default function AuthCombined({ initialTab } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useUser();
  const [error, setError] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab === "signup" ? "signup" : "login");

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const tabFromUrl = params.get("tab");
    if (tabFromUrl === "signup") setActiveTab("signup");
    if (tabFromUrl === "login") setActiveTab("login");
  }, [location.search]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsGoogleLoading(true);
    setError("");
    try {
      const response = await DataService.loginWithGoogle(credentialResponse.credential);
      if (response.status === 200) {
        try { trackEvent("auth", "google_auth_success"); } catch (_) {}
        try { await updateUser?.(); } catch (_) {}
        navigate(getPostAuthRedirectTarget(location), { replace: true });
      } else {
        setError("Connexion ou inscription Google échouée");
        try { trackEvent("auth", "google_auth_error"); } catch (_) {}
      }
    } catch (err) {
      setError("Connexion ou inscription Google échouée");
      try { trackEvent("auth", "google_auth_exception"); } catch (_) {}
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Connexion Google échouée");
    try { trackEvent("auth", "google_auth_error"); } catch (_) {}
  };

  const tabVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.2, ease: "easeOut" },
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 app-page-gradient">
      <div className="w-full max-w-4xl space-y-6">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <img src={LogoBlue} alt="Peppa" className="h-7" />
          </div>
          {/* <h1 className="text-3xl text-foreground font-bold text-high-contrast">Connexion ou inscription</h1> */}
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Trouvez rapidement les financements adaptés à vos projets.
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="max-w-2xl mx-auto">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2 max-w-2xl mx-auto">
          <div className="relative w-full">
            <Button
              type="button"
              variant="outline"
              className="mx-auto border-border/50 bg-card/90 backdrop-blur-sm hover:bg-accent/50 flex items-center justify-center gap-2 text-foreground"
              disabled={isGoogleLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-4 w-4">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,14,24,14c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.022C9.505,39.556,16.227,44,24,44z" />
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.236-2.231,4.166-3.994,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
              </svg>
              <span>{isGoogleLoading ? "Connexion en cours..." : "Continuer avec Google"}</span>
            </Button>
            <div className="google-overlay">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="outline"
                shape="rectangular"
                text="continue_with"
                size="large"
                width="520"
              />
            </div>
          </div>
        </div>

        <div className="glass-card border-muted border shadow-2xl w-full bg-card/80 rounded-2xl overflow-hidden">
          <div className="flex gap-2 px-4 pt-3 border-b border-border/60 bg-card/60">
            {[
              { key: "login", label: "Connexion" },
              { key: "signup", label: "Créer un compte" },
            ].map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 text-sm md:text-base font-semibold text-center pt-2 pb-3 border-b-2 transition-colors duration-150
                    ${isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <motion.div layout className="p-4 md:p-6">
            <AnimatePresence mode="wait" initial={false}>
              {activeTab === "login" && (
                <motion.div
                  key="login"
                  layout
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={tabVariants.transition}
                  variants={tabVariants}
                >
                  <LoginModern embedded noCard showGoogleButton={false} showLogo={false} />
                </motion.div>
              )}
              {activeTab === "signup" && (
                <motion.div
                  key="signup"
                  layout
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={tabVariants.transition}
                  variants={tabVariants}
                >
                  <SignUpFormModern embedded noCard showGoogleButton={false} showLogo={false} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
}